import { cloneElement, isValidElement, memo, useContext, useEffect, useMemo, useState } from 'react';
import { Collection, composeRenderProps } from 'react-aria-components';
import { Header, ListBox, ListBoxSection, ProgressBar } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import { SearchResultsContext } from './contexts';
import type { SearchResultsData, SearchResultsProps, Section, SectionedData, WithKey } from './types';

import styles from './ResourceSelector.module.css';

// Default function to extract a unique key from any object
const defaultGetItemKey = <T extends object>(item: T): string => {
  const itemAsRecord = item as Record<string, unknown>;
  // Try common id patterns, fall back to JSON stringify
  return String(itemAsRecord.key || itemAsRecord.id || itemAsRecord._id || itemAsRecord.uuid || JSON.stringify(item));
};

// Helper function to check if data is sectioned
const isSectionedData = <T extends object>(items: SearchResultsData<T>): items is SectionedData<T> => {
  return (
    Array.isArray(items) &&
    items.length > 0 &&
    typeof items[0] === 'object' &&
    items[0] !== null &&
    'name' in items[0] &&
    'items' in items[0]
  );
};

// Slot component for ResourceSelector
function SearchResultsInternal<T extends object = object>({
  items,
  children,
  onDetailsItemChange,
  selectedKeys,
  getItemKey = defaultGetItemKey,
  renderEmptyState,
  isLoading = false,
  isLoadingMore,
  isError = false,
  onLoadMore,
  hasMore,
  resultsAreaClassName,
  slot,
  ...props
}: SearchResultsProps<T>) {
  // Get props from context when used as a slot
  const contextProps = useContext(SearchResultsContext);

  // Merge local props with context props, local props take precedence
  const mergedProps = {
    selectedKeys: selectedKeys ?? contextProps?.selectedKeys,
    onSelectionChange: props.onSelectionChange ?? contextProps?.onSelectionChange,
    selectionMode: props.selectionMode ?? contextProps?.selectionMode,
    onDetailsItemChange: onDetailsItemChange ?? contextProps?.onDetailsItemChange,
    renderEmptyState: renderEmptyState ?? contextProps?.renderEmptyState,
    isLoadingMore: isLoadingMore ?? contextProps?.isLoadingMore ?? false,
    onLoadMore: onLoadMore ?? contextProps?.onLoadMore,
    hasMore: hasMore ?? contextProps?.hasMore ?? false,
  };

  // Use merged props for infinite scroll logic
  const shouldEnableInfiniteScroll = mergedProps.onLoadMore && mergedProps.hasMore;

  // State for tracking selection changes
  const [lastSelectedKey, setLastSelectedKey] = useState<string | null>(null);
  const [listBoxElement, setListBoxElement] = useState<HTMLDivElement | null>(null);

  // Use a callback ref to get better control over when the element is set
  const listBoxRef = (element: HTMLDivElement | null) => {
    setListBoxElement(element);
  };

  // Process items - handle both flat and sectioned data
  const processedData = useMemo(() => {
    if (!items) {
      return { isFlat: true, flatItems: [], sectionedItems: [], keyToOriginalItem: new Map() };
    }

    // Map to track original items by their keys
    const keyToOriginalItem = new Map<string, T>();

    if (isSectionedData<T>(items)) {
      // Sectioned data
      const sectionsWithKeys: Array<Section<WithKey<T>>> = items.map((section) => ({
        name: section.name,
        items: section.items.map((item) => {
          const key = 'key' in item ? item.key : getItemKey(item);
          keyToOriginalItem.set(String(key), item);
          return {
            ...item,
            key: String(key),
          };
        }),
      }));

      // Also flatten for compatibility with existing logic - items already have keys
      const allFlatItems: Array<WithKey<T>> = sectionsWithKeys.flatMap((section) => section.items);

      return {
        isFlat: false,
        flatItems: allFlatItems,
        sectionedItems: sectionsWithKeys,
        keyToOriginalItem,
      };
    } else {
      // Flat data
      const itemsArray = Array.from(items);
      const flatItems: Array<WithKey<T>> = itemsArray.map((item) => {
        const key = 'key' in item ? item.key : getItemKey(item);
        keyToOriginalItem.set(String(key), item);
        return {
          ...item,
          key: String(key),
        };
      });

      return {
        isFlat: true,
        flatItems,
        sectionedItems: [],
        keyToOriginalItem,
      };
    }
  }, [items, getItemKey]);

  const itemsWithKeys = processedData.flatItems;

  const firstItemKey = useMemo(() => (itemsWithKeys.length > 0 ? itemsWithKeys[0].key : null), [itemsWithKeys]);

  // Track last selected item when selection changes
  useEffect(() => {
    if (mergedProps.selectedKeys && mergedProps.selectedKeys !== 'all') {
      // Find the most recently selected item that's still in the current items
      const selectedKeysArray = Array.from(mergedProps.selectedKeys);
      if (selectedKeysArray.length > 0) {
        // Use the last selected key that's still in our current items
        const lastValidSelectedKey = selectedKeysArray
          .reverse()
          .find((key) => itemsWithKeys.some((item) => item.key === key));

        if (lastValidSelectedKey) {
          setLastSelectedKey(String(lastValidSelectedKey));
        }
      } else {
        // No items selected, clear last selected
        setLastSelectedKey(null);
      }
    }
  }, [mergedProps.selectedKeys, itemsWithKeys]);

  // Listen for keyboard navigation to update details panel
  useEffect(() => {
    if (!listBoxElement) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if we're navigating with arrow keys or tab
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Tab') {
        // Give React Aria time to update the selection, then check what's focused
        setTimeout(() => {
          const focusedElement = document.activeElement as HTMLElement;

          if (focusedElement && listBoxElement.contains(focusedElement)) {
            // Find the closest element with data-key
            let element = focusedElement;
            while (element && element !== listBoxElement) {
              const itemKey = element.getAttribute('data-key');
              if (itemKey) {
                // Call parent to update details panel
                const originalItem = processedData.keyToOriginalItem.get(itemKey);
                if (originalItem && mergedProps.onDetailsItemChange) {
                  mergedProps.onDetailsItemChange(originalItem);
                }
                return;
              }
              element = element.parentElement as HTMLElement;
            }
          }
        }, 0);
      }
    };

    listBoxElement.addEventListener('keydown', handleKeyDown);

    return () => {
      listBoxElement.removeEventListener('keydown', handleKeyDown);
    };
  }, [listBoxElement, processedData.keyToOriginalItem, mergedProps.onDetailsItemChange]);

  // Simple scroll listener for infinite scroll
  useEffect(() => {
    if (!shouldEnableInfiniteScroll || !listBoxElement) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = listBoxElement;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceFromBottom <= 32 && mergedProps.onLoadMore) {
        mergedProps.onLoadMore();
      }
    };

    listBoxElement.addEventListener('scroll', handleScroll);

    return () => {
      listBoxElement.removeEventListener('scroll', handleScroll);
    };
  }, [shouldEnableInfiniteScroll, mergedProps.onLoadMore, listBoxElement]);

  // Handle default details item (only for initial load, no focus/hover dependencies)
  const defaultDetailsItem = useMemo(() => {
    let selectedKey = null;

    // 1. Last selected - priority
    if (lastSelectedKey && itemsWithKeys.some((item) => item.key === lastSelectedKey)) {
      selectedKey = lastSelectedKey;
    }
    // 2. First in list (fallback) - lowest priority
    else {
      selectedKey = firstItemKey;
    }

    if (!selectedKey) {
      return null;
    }

    const originalItem = processedData.keyToOriginalItem.get(selectedKey);
    return originalItem || null;
  }, [lastSelectedKey, firstItemKey, itemsWithKeys, processedData.keyToOriginalItem]);

  // Notify parent of default details changes (initial load only)
  useEffect(() => {
    if (defaultDetailsItem) {
      mergedProps.onDetailsItemChange?.(defaultDetailsItem);
    } else {
      mergedProps.onDetailsItemChange?.(null);
    }
  }, [defaultDetailsItem, mergedProps.onDetailsItemChange]);

  // Enhanced children function that adds hover tracking
  const enhancedChildren = (itemWithKey: WithKey<T>) => {
    const itemKey = itemWithKey.key;
    // Get the original item without any modifications
    const originalItem = processedData.keyToOriginalItem.get(itemKey) || itemWithKey;
    const originalChild = children(originalItem);

    // Type guard to ensure we have a valid React element
    if (!isValidElement(originalChild)) {
      return originalChild;
    }

    // Add hover events and data-key for focus tracking
    const enhancedProps = {
      onHoverStart: () => {
        // Call the parent to handle details
        if (originalItem && mergedProps.onDetailsItemChange) {
          mergedProps.onDetailsItemChange(originalItem);
        }
      },
      onHoverEnd: () => {
        // No state changes needed - details persist
      },
      'data-key': itemKey,
    };

    return cloneElement(originalChild, enhancedProps);
  };

  // Show loading spinner if isLoading is true
  if (isLoading) {
    return (
      <div className={clsx(styles.resultsArea, resultsAreaClassName)}>
        <div className={styles.loadingContainer}>
          <ProgressBar aria-label="Loading..." isIndeterminate size="small" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={clsx(styles.resultsArea, resultsAreaClassName)}>
        <div className={styles.loadingContainer}>
          <span className={styles.errorMessage}>
            <Icon name="warning" className={styles.errorIcon} />
            Failed to load
          </span>
        </div>
      </div>
    );
  }

  // Show empty state if no items and renderEmptyState is provided
  if (itemsWithKeys.length === 0 && mergedProps.renderEmptyState) {
    return mergedProps.renderEmptyState();
  }

  return (
    <div className={clsx(styles.resultsArea, resultsAreaClassName)}>
      {processedData.isFlat ? (
        // Flat data - current behavior
        <ListBox<WithKey<T>>
          {...props}
          ref={listBoxRef}
          items={itemsWithKeys}
          selectedKeys={mergedProps.selectedKeys}
          onSelectionChange={mergedProps.onSelectionChange}
          selectionMode={mergedProps.selectionMode}
          className={composeRenderProps(props.className, (className) => clsx(className, styles.results))}
          aria-label={props['aria-label'] || 'Search results'}
        >
          {enhancedChildren}
        </ListBox>
      ) : (
        // Sectioned data - new behavior
        <ListBox
          {...props}
          ref={listBoxRef}
          selectedKeys={mergedProps.selectedKeys}
          onSelectionChange={mergedProps.onSelectionChange}
          selectionMode={mergedProps.selectionMode}
          className={composeRenderProps(props.className, (className) => clsx(className, styles.results))}
          aria-label={props['aria-label'] || 'Search results'}
        >
          {/* Some consumers intentionally provide unnamed sections; render items without an empty header row. */}
          {processedData.sectionedItems.map((section, index) => (
            <ListBoxSection key={`${section.name || 'section'}-${index}`} id={section.name || `section-${index}`}>
              {section.name ? <Header>{section.name}</Header> : null}
              <Collection items={section.items}>{enhancedChildren}</Collection>
            </ListBoxSection>
          ))}
        </ListBox>
      )}
      {mergedProps.isLoadingMore && (
        <div className={styles.bottomLoadingContainer}>
          <ProgressBar aria-label="Loading..." isIndeterminate size="small" />
        </div>
      )}
    </div>
  );
}

// Export memoized version to prevent unnecessary re-renders of expensive child components
export const SearchResults = memo(SearchResultsInternal) as typeof SearchResultsInternal;
